/**
* @typedef Directive:Lazyload.VueBindings
* @property {Directive:Lazyload.LazyloadBindings} value
*/
/**
* @typedef Directive:Lazyload.SetItem
* @property {number} width - Width of the image
* @property {number} height - Height of the image
* @property {'jpeg'} format - Format of the image
* @property {'xs'|'sm'|'md'|'lg'|'xl'|'xxl'} breakpoint - Minimal breakpoint for this set to be used
*/
/**
* @typedef {Object} Directive:Lazyload.SettingsObject
* @property {string} src - Image source
* @property {Array<Directive:Lazyload.SetItem>} [set] - Set of options for each breakpoint, can be set by a variant
* @property {'start'|'end'|'center'|'top left'|'top center'|'top right'|'center left'|'center center'|'center right'|'bottom left'|'bottom center'|'bottom right'} [align] - Alignemt of background image
* @property {'wrap'|'crop'} [fit] - Fit of background image
* @property {'portrait-tall'|'portrait-short'|'landscape'|'square'} [ratio] - Use a ratio to get the height of a image based on the width
* @property {Function} [updateCallback] - Callback from the Vue component to be executed when the image is loaded
* @property {boolean} imageLoaded - Sets true when image is loaded
* @property {boolean} imgInView - Set true when image is in view
* @property {boolean} canLoad - When callback is present, sets if image can load
* @property {boolean} callbackCalled - Set true when callback is called
*/
/**
* @typedef {Object} Directive:Lazyload.LazyloadBindings
* @property {string} src - Image source
* @property {Array<SetItem>} [set] - Set of options for each breakpoint, can be set by a variant
* @property {'start'|'end'|'center'|'top left'|'top center'|'top right'|'center left'|'center center'|'center right'|'bottom left'|'bottom center'|'bottom right'} [align] - Alignemt of background image
* @property {'wrap'|'crop'} [fit] - Fit of background image
* @property {'portrait-tall'|'portrait-short'|'landscape'|'square'} [ratio] - Use a ratio to get the height of a image based on the width.
* @property {string} [variant] - Variant to be used for settings options
* @property {Function} [updateCallback] - Callback from the Vue component to be executed when the image is loaded
*/
/**
 * @typedef {Object} Directive:Lazyload.SettingsResponse
 * @property {HTMLImageElement} img
 * @property {Directive:Lazyload.SettingsObject} settings
 */
import {intersectionViewportObserver} from '@utils/intersectionViewportObserver'
import {debounce} from '@utils/timing'
import {breakpoints} from '@store/responsive'
/**
 * @module Directive:Lazyload
 * @description
 * Directive to be used in a picture tag, it not only lazy load images but also create a source set of responsive sources
 *
 * [check guide]{@link /docs/responsiveimages}
 *
 * **Example:**
 * ```html
 * // Using variant
 * <picture
 *   v-lazyload="{
 *     src: 'https://photos.mandarinoriental.com/is/image/MandarinOriental/bangkok-exterior-river-of-kings-5',
 *     variant: 'my-variant'
 *   }"
 * >
 *   <img src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" />
 * </picture>
 * // Manual settings
 * <picture
 *   v-lazyload="{
 *     src: 'https://photos.mandarinoriental.com/is/image/MandarinOriental/bangkok-exterior-river-of-kings-5',
 *     set: [{ width: 300, height: 300, breakpoint: 'xs' }],
 *     fit: 'crop',
 *     align: 'center'
 *   }"
 * >
 *   <img src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" />
 * </picture>
 * ```
 */
const ratios = {
  'portrait-tall': 1.5,
  'portrait-short': 1.333333333333333,
  'landscape': 0.5625,
  'square': 1
}

/**
* Set picture tag background alignment
* @param {HTMLElement} el - <picture\> tag element
* @param {'start'|'end'|'center'|'top left'|'top center'|'top right'|'center left'|'center center'|'center right'|'bottom left'|'bottom center'|'bottom right'} align - Alignment of background image
*/
const alignImage = (el, align) => {
  const {height, width} = el.getBoundingClientRect()
  const aspectRatio = width / height
  if (align === 'start') {
    el.style.backgroundPosition = aspectRatio > 1 ? 'left' : 'top'
  } else if (align === 'end') {
    el.style.backgroundPosition = aspectRatio > 1 ? 'right' : 'bottom'
  } else if (align === 'center' || align === 'middle') {
    el.style.backgroundPosition = 'center'
  } else {
    el.style.backgroundPosition = align !== '' ? align : "center"
  }
}

/**
* Get the current image in use from the set
* based on the window width
* @param {Array<Directive:Lazyload.SetItem>} set - Set of properties for each breakpoint source
* @returns {Directive:Lazyload.SetItem} Set item
*/
const getCurrentSet = (set = []) => {
  const windowWidth = window.innerWidth
  const sortedBreakpoints = [...set].sort((a, b) => breakpoints[a.breakpoint] > breakpoints[b.breakpoint] ? 1 : -1)
  return sortedBreakpoints.filter(s => breakpoints[s.breakpoint] <= windowWidth)[0]
}

/**
* Set's a temporary height for the <picture> tag before the image is loaded
* @param {HTMLElement} el - <picture\> tag
* @param {Directive:Lazyload.SettingsObject} settings
*/
const setTemporaryHeight = (el, settings) => {
  const {ratio, set} = settings
  const currentSet = set.length ? getCurrentSet(set) : null
  const elBoundingRect = el.getBoundingClientRect()
  const {width, height} = (currentSet || elBoundingRect)
  const calculatedHeight = ratio ? getRatioHeight(width, ratio) : height
  el.style.width = `${width}px`
  el.style.height = `${calculatedHeight}px`
}

/**
* Calculate the height based on a ratio and the width
* @param {number} width
* @param {string} ratio
* @returns {number} height
*/
const getRatioHeight = (width, ratio) => width * ratios[ratio]

/**
 * Update the source elements to load image and update background styles on <picture> tag
 * @param {HTMLElement} el - <picture\> tag
 * @param {HTMLElement} img - <img\> tag
 * @param {Directive:Lazyload.SettingsObject} settings
 */
const updateBackground = (el, img, settings) => {
  const {
    fit = 'crop',
    align = 'center',
    updateCallback
  } = settings
  if (fit === 'crop') {
    el.style.backgroundSize = 'cover'
  } else if (fit === 'wrap') {
    el.style.backgroundSize = 'contain'
  } else if (fit === 'auto') {
    el.style.backgroundSize = 'auto'
  }
  el.style.backgroundRepeat = 'no-repeat'
  alignImage(el, align)
  el.style.backgroundImage = `url('${
    img.currentSrc
  }')`
  const imgCanLoad = (settings.imageLoaded && settings.imgInView) || (updateCallback && el.canLoad) || !updateCallback
  if (imgCanLoad && !el.classList.contains('loaded')) {
    createSources(settings.src, settings.set, el, settings.ratio, settings.isfallback)
  }
  if (updateCallback && !settings.callbackCalled && el.canLoad && el.sourcesCreated && img.imageLoaded) {
    updateCallback()
    settings.callbackCalled = true
    el.callbackCalled = true
  }
}

/**
 * Generate the actual source string based on Scene7 API to get the image in a specified width / height
 * @param {string} src - image source
 * @param {number} width
 * @param {number} height
 * @param {string} ratio
 * @param {string} format
 * @param {boolean} isfallback
 * @returns {string} - formated string
 */
const generateSourceString = (src, width, height, ratio, format = 'jpeg', isfallback) => {

  //some lazy loading markup is coming from .net and we know is it scene7 or frontify
  //but in some cases it's triggered by FE code, for ex. image srs is coming in json
  //so to avoid those cases both frontify and scene7 image size parameters will be sent
  let paramMapping = {
    width: 'width',
    height: 'height'
  }
  let paramMappingS7 = {
    width: 'wid',
    height: 'hei'
  }

  const calculatedHeight = ratio ? getRatioHeight(width, ratio) : height
  const hasWidthQuery = width ? '&' : ''
  const heightQuery = calculatedHeight ? `${hasWidthQuery}${paramMapping.height}=${calculatedHeight}&${paramMappingS7.height}=${calculatedHeight}` : ''
  const widthQuery = width ? `${paramMapping.width}=${width}&${paramMappingS7.width}=${width}` : ''
  // frontify crop is possible only if height and width are set!
  const frontifyCropQuery = (heightQuery && widthQuery) ? '&fp=0.5,0.5&crop=fp' : ''
  const queryString = `?${widthQuery}${heightQuery}&fmt=${format}&op_usm=1,1,5,0&resMode=sharp2&fit=crop&qlt=75,0${frontifyCropQuery}`
  return `${src}${queryString}`
}

/**
 * Creates source elements inside the <picture\> tag for each of the configured SetItem
 * @param {string} src
 * @param {Array<Directive:Lazyload.SetItem>} set
 * @param {HTMLElement} el
 * @param {string} ratio
 * @param {string} isfallback
 */
const createSources = (src, set, el, ratio, isfallback) => {
  if (el.sourcesCreated) return
  if (!src && !Array.isArray(set)) {
    return new Error('source set malformed')
  }
  set.forEach((s, i) => {
    const width = typeof s === 'number' ? s : s.width
    const height = typeof s === 'number' ? 0 : s.height
    const breakpointKey = s.breakpoint || 'xs'
    const format = s.format || 'jpeg'
    const source = document.createElement('SOURCE')
    source.type = 'image/' + format
    source.media = `(min-width: ${
      breakpoints[breakpointKey]
    }px)`
    source.srcset = generateSourceString(src, width, height, ratio, format, isfallback)
    el.prepend(source)
  })
  el.style.width = ''
  el.style.height = ''
  el.sourcesCreated = true
}
/**
* Generate settings object
* @param {HTMLElement} el - <picture\> tag element
* @param {VueBindings} binding
* @param {VueInstance} vnode
* @return {Directive:Lazyload.SettingsResponse}
*/
const generateSettings = (el, binding, vnode) => {
  const img = el.querySelector('img')
  const {variant, src} = (binding.value || {})
  let settings = {
    src,
    set: binding.value.set,
    ratio: binding.value.ratio,
    fit: binding.value.fit,
    align: binding.value.align,
    updateCallback: binding.value.updateCallback,
    canLoad: typeof binding.value.canLoad === 'boolean' ? binding.value.canLoad : true,
    imageLoaded: img.imageLoaded ?? false,
    imgInView: img.imgInView ?? false,
    callbackCalled: el.callbackCalled ?? false,
    isfallback: binding.value.isfallback ?? true
  }
  el.canLoad = typeof binding.value.canLoad === 'boolean' ? binding.value.canLoad : true
  const store = vnode.context.$store
  if (variant || ! settings.set || ! settings.set.length) {
    const getProperty = store.getters['variants/getVariantSettings']
    const variantSettings = getProperty('lazyload', variant, 'settings')
    settings.set = variantSettings.set || settings.set
    settings.ratio = variantSettings.ratio || settings.ratio
    settings.fit = variantSettings.fit || settings.fit
    settings.align = settings.align || variantSettings.align
    settings.align = settings.align && settings.align !== ' ' ? settings.align : variantSettings.align
  }
  if (settings.set) {
    const breakpointRations = {}
    settings.set.forEach((s) => {
      breakpointRations[s.breakpoint] = parseInt(s.height) / parseInt(s.width)
    })
    const elWidth = el.getBoundingClientRect().width
    const initialImageHeight = store.getters['responsive/getValueForBestBreakpoint'](breakpointRations) * elWidth
    img.style.height = initialImageHeight + 'px'
  }
  return {img, settings}
}

/**
* Initialize the direcitve binding
* @param {HTMLElement} el - <picture\> tag element
* @param {Directive:Lazyload.VueBindings} binding
* @param {Directive:Lazyload.VueInstance} vnode
*/
const initBackgroundImage = (el, binding, vnode) => {
  const {img, settings} = generateSettings(el, binding, vnode)
  if (settings.ratio) {
    setTemporaryHeight(el, settings)
  }
  if (settings.updateCallback && el.canLoad) {
    createSources(settings.src, settings.set, el, settings.ratio, settings.isfallback)
  }
  img.onload = function () {
    if (el.sourcesCreated) {
      settings.imageLoaded = true
      img.imageLoaded = true
      el.classList.add('loaded')
      img.style.height = ''
      updateBackground(el, img, settings)
    }
  }
  img.onerror = function () {
    if (settings.updateCallback && el.canLoad) {
      settings.updateCallback()
    }
  }

  intersectionViewportObserver(el, {checkIsVisible: false}).then(() => {

    el.listener = () => {
      debounce(updateBackground.bind(el, img, settings), 200)
    }

    window.addEventListener('resize', el.listener)

    setTimeout(() => {
      settings.imgInView = true
      img.imgInView = true
      updateBackground(el, img, settings)
    }, 100)
  })
}

/**
 * Remove listeners
 * @param {HTMLElement} el - <picture/> tag element
 */
const destroyBackgroundImage = (el) => {
  if (el.listener) {
    window.removeEventListener('resize', el.listener)
  }
}

export const lazyload = {
  name: 'lazyload',
  inserted: (el, binding, vnode) => {
    initBackgroundImage(el, binding, vnode)
  },

  update: (el, binding, vnode) => {
    const { img, settings } = generateSettings(el, binding, vnode)
    updateBackground(el, img, settings)
  },

  unbind(el) {
    destroyBackgroundImage(el)
  }
}
